import React from 'react';
import { F } from '../../styles/theme';
import { COIN_MAP } from '../../utils/coingecko';

// Curated gradient palette per coin. XRP/HBAR mit lesbaren Brand-Akzenten
// statt Schwarz, weil reines Schwarz auf dunklem Hintergrund verschwindet.
const COIN_GRADIENTS = {
  BTC:  ['#F7931A', '#8B5A0A'],
  ETH:  ['#627EEA', '#3C4FAA'],
  SOL:  ['#14F195', '#9945FF'],
  XRP:  ['#00AAE4', '#005C7A'],
  ADA:  ['#0033AD', '#001E66'],
  DOT:  ['#E6007A', '#7A0042'],
  LINK: ['#2A5ADA', '#1A3A8A'],
  AVAX: ['#E84142', '#8A2526'],
  MATIC:['#8247E5', '#4A2885'],
  HBAR: ['#7B68EE', '#3F2F8A'],
  USDT: ['#26A17B', '#0E5E45'],
  USDC: ['#2775CA', '#0E437A'],
  BNB:  ['#F3BA2F', '#9B7A14'],
  DOGE: ['#C2A633', '#7B6720'],
  LTC:  ['#A6A9AA', '#5B5E5F'],
  TRX:  ['#FF060A', '#8B0306'],
  XLM:  ['#7D00FF', '#3F0080'],
  ATOM: ['#2E3148', '#6F7390'],
  NEAR: ['#00C08B', '#005F45'],
  APT:  ['#1ABC9C', '#0E5F4F'],
  ARB:  ['#28A0F0', '#13507A'],
  OP:   ['#FF0420', '#8A0212'],
  FIL:  ['#0090FF', '#004580'],
  ICP:  ['#F15A24', '#7A2D12'],
  ALGO: ['#9DA3B4', '#4A4F5C'],
  SHIB: ['#FFA409', '#7F5204'],
  PEPE: ['#52B788', '#1B4332'],
  SUI:  ['#4DA2FF', '#1F5BAA'],
  TIA:  ['#7B2BF9', '#3D157D'],
  INJ:  ['#00F2FE', '#007A82'],
  RNDR: ['#FF6B6B', '#8A2F2F'],
  ONDO: ['#7B68EE', '#3F2F8A'],
  BCH:  ['#0AC18E', '#055F47'],
  ETC:  ['#3AB83A', '#1D5C1D'],
  XMR:  ['#FF6600', '#7F3300'],
  AAVE: ['#B6509E', '#5B284F'],
  MKR:  ['#1AAB9B', '#0D554E'],
  UNI:  ['#FF007A', '#80003D'],
  CRV:  ['#FFD700', '#806B00'],
  LDO:  ['#00A3FF', '#005180'],
  FET:  ['#1F2A47', '#5A6BB3'],
  JUP:  ['#FE9A00', '#7F4D00'],
  WIF:  ['#D78A55', '#6B452A'],
  BONK: ['#FFAA00', '#805500'],
  FLOKI:['#F0B90B', '#785C05'],
  ENA:  ['#000F2E', '#5A6F9E'],
  PYTH: ['#E6DAFE', '#7A6FAA'],
  JTO:  ['#1FCAF0', '#0F6578'],
  WLD:  ['#000000', '#6B6B6B'],
  RUNE: ['#33FF99', '#19804C'],
  KAS:  ['#70C7BA', '#386360'],
  STX:  ['#5546FF', '#2B2380'],
  IMX:  ['#0AB2C1', '#055960'],
  GRT:  ['#6F4CFF', '#372680'],
  THETA:['#2AB8E6', '#155C73'],
  SAND: ['#00ADEF', '#005678'],
  MANA: ['#FF2D55', '#80162B'],
  AXS:  ['#0055D5', '#002B6A'],
  ENS:  ['#5298FF', '#294C80'],
};

// Hash a string to a stable, vibrant HSL color. Luminance bei 60% +
// Saettigung 70% sorgt fuer ausreichend Kontrast in beiden Themes
// (nicht zu dunkel im Dark-Mode, nicht zu blass im Light-Mode).
function hashColor(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 70%, 60%)`;
}

export default function CoinGlyph({ sym, size = 40 }) {
  if (!sym) return <div style={{ width: size, height: size, borderRadius: size / 2, background: '#222' }} />;
  const upper = String(sym).toUpperCase();
  const fallback = hashColor(upper);
  const grad = COIN_GRADIENTS[upper] || [fallback, fallback];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontFamily: F.display,
        fontWeight: 700,
        fontSize: size * 0.34,
        letterSpacing: '-0.04em',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.18)',
        flexShrink: 0,
      }}
    >
      {upper[0]}
    </div>
  );
}

// Relative Luminanz (sRGB) — 0 = schwarz, 1 = weiss.
function luminance(hex) {
  if (!hex || !hex.startsWith('#')) return 0.5;
  const h = hex.slice(1);
  const expand = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(expand.slice(0, 2), 16) / 255;
  const g = parseInt(expand.slice(2, 4), 16) / 255;
  const b = parseInt(expand.slice(4, 6), 16) / 255;
  const lin = v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function coinColor(sym) {
  const upper = String(sym || '').toUpperCase();
  if (!upper) return '#888';
  const curated = COIN_GRADIENTS[upper] ? COIN_GRADIENTS[upper][0] : null;
  if (!curated) return hashColor(upper);
  // Schutz: kuratierte Farbe darf nicht zu hell (unsichtbar auf Light-Card)
  // oder zu dunkel (unsichtbar auf Dark-Card) sein. Faellt auf Hash zurueck.
  const L = luminance(curated);
  if (L > 0.82 || L < 0.06) return hashColor(upper);
  return curated;
}

export function coinName(sym) {
  if (!sym) return '';
  const upper = String(sym).toUpperCase();
  const meta = COIN_MAP?.[upper];
  return meta?.name || upper;
}
